import React from 'react';
import { Box } from '@mui/material';
import { usePermissions } from '../../../../contexts/PermissionContext';
import VisualTreeView from '../../../../components/LocationTree/VisualTreeView';
import { ContentSkeleton } from '../../../../components/SkeletonLoaders';
import EmptyState from '../../../../components/EmptyState';
import ErrorMessage from '../../../../components/ErrorMessage';
import { LocationOn as LocationIcon, Add as AddIcon } from '@mui/icons-material';

/**
 * Network View - wraps existing VisualTreeView canvas
 * Full-width interactive graph visualization
 */
const NetworkView = ({
  locations,
  loading,
  error,
  selectedLocation,
  onLocationSelect,
  onLocationEdit,
  onLocationDelete,
  onLocationAdd,
  onRetry,
  t
}) => {
  const { hasPermission } = usePermissions();

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <ContentSkeleton />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorMessage
          error={error}
          title={t ? t('LocationManagement.error_loading_locations') : 'Error Loading Locations'}
          context="locations"
          onRetry={onRetry}
          showDetails={true}
        />
      </Box>
    );
  }

  if (locations.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          type="generic"
          title={t ? t('LocationManagement.no_locations') : 'No Locations'}
          description={t ? t('LocationManagement.no_locations_desc') : 'No locations have been created yet. Add your first location to get started.'}
          icon={<LocationIcon sx={{ fontSize: 80, color: 'text.disabled' }} />}
          action={hasPermission('locations.create') ? {
            label: t ? t('LocationManagement.add_location') : 'Add Location',
            onClick: () => onLocationAdd(null),
            icon: <AddIcon />
          } : null}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', height: 'calc(100vh - 230px)' }}>
      <VisualTreeView
        locations={locations}
        selectedLocationId={selectedLocation?.id || selectedLocation?._id || selectedLocation?.location_UUID}
        onLocationSelect={onLocationSelect}
        onLocationEdit={onLocationEdit}
        onLocationDelete={onLocationDelete}
        onLocationAdd={onLocationAdd}
        selectedLocation={selectedLocation}
      />
    </Box>
  );
};

export default NetworkView;
